import { useTranslation } from 'react-i18next';
import { Check, CircleAlert, ExternalLink, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StorybookPrint, StorybookPrintStatus } from '@/lib/storybook-api';
import { formatCountryList } from '@/lib/storybook-utils';

const STEPS = ['pending', 'submitted', 'accepted', 'in_production', 'shipped'] as const;
const PROBLEM_STATUSES: StorybookPrintStatus[] = ['rejected', 'canceled', 'error'];

function stepIndex(status: StorybookPrintStatus): number {
  switch (status) {
    case 'pending':
    case 'submitting':
      return 0;
    case 'submitted':
      return 1;
    case 'accepted':
      return 2;
    case 'in_production':
      return 3;
    case 'shipped':
      return 4;
    default:
      return -1;
  }
}

/** Only real web links become clickable tracking links. */
function safeHttpUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export function StorybookPrintTimeline({ print }: { print: StorybookPrint }) {
  const { t, i18n } = useTranslation();
  const problem = PROBLEM_STATUSES.includes(print.status);
  const current = stepIndex(print.status);
  const links = (print.trackingUrls || []).map(safeHttpUrl).filter((url): url is string => Boolean(url));

  return (
    <section
      aria-labelledby="sb-print-title"
      data-testid="storybook-print-timeline"
      className="rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[hsl(45_95%_85%)] text-[hsl(30_70%_28%)]">
          <Truck className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 id="sb-print-title" className="text-xl font-semibold">{t('storybookPage.book.print.title')}</h2>
          {print.country && (
            <p className="text-sm text-muted-foreground">
              {t('storybookPage.book.print.destination', { country: formatCountryList([print.country], i18n.language) })}
            </p>
          )}
        </div>
      </div>

      {problem ? (
        <div role="status" className="mt-5 flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <p>{t('storybookPage.book.print.problem')}</p>
        </div>
      ) : (
        <ol className="mt-6 grid gap-4 sm:grid-cols-5 sm:gap-2">
          {STEPS.map((step, index) => {
            const done = index < current || (index === current && step === 'shipped');
            const active = index === current && step !== 'shipped';
            return (
              <li
                key={step}
                aria-current={index === current ? 'step' : undefined}
                className="relative flex items-center gap-3 sm:flex-col sm:text-center"
              >
                {index < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute left-[1.05rem] top-9 h-[calc(100%-0.5rem)] w-0.5 sm:left-[calc(50%+1.25rem)] sm:top-[1.05rem] sm:h-0.5 sm:w-[calc(100%-2.5rem)]',
                      index < current ? 'bg-primary' : 'bg-border',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold',
                    done && 'border-primary bg-primary text-primary-foreground',
                    active && 'border-primary bg-card text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]',
                    !done && !active && 'border-border bg-card text-muted-foreground',
                  )}
                >
                  {done ? <Check className="h-4 w-4" aria-hidden="true" /> : index + 1}
                </span>
                <span className={cn('text-sm font-semibold', index > current && 'text-muted-foreground')}>
                  {t(`storybookPage.book.print.statuses.${step}`)}
                  {done && <span className="sr-only"> {t('storybookPage.book.print.doneSr')}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {print.carrier && (
        <p className="mt-5 text-sm">{t('storybookPage.book.print.carrier', { carrier: print.carrier })}</p>
      )}
      {links.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {links.map((url, index) => (
            <li key={url}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-4 py-2 text-sm font-semibold text-primary hover:bg-muted"
              >
                {links.length > 1
                  ? t('storybookPage.book.print.trackingN', { n: index + 1 })
                  : t('storybookPage.book.print.tracking')}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      )}
      {!problem && print.status !== 'shipped' && (
        <p className="mt-4 text-xs text-muted-foreground">{t('storybookPage.book.print.shippingTime')}</p>
      )}
    </section>
  );
}
